/**
 * Formatting and ordering for the admin dashboard's team-time and time-off
 * cards, kept out of the component so it can be tested without rendering.
 *
 * Clock times are shown in the TENANT's timezone, like every other hours
 * screen: "clocked in at 8:02 AM" means 8:02 where the employee is, not where
 * the admin happens to be reading it.
 */
import { localDayKey } from '@lalohr/utils';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
const LEAVE_TYPE_LABELS = {
    pto: 'PTO',
    vacation: 'Vacation',
    sick: 'Sick Leave',
    personal: 'Personal',
    bereavement: 'Bereavement',
    jury_duty: 'Jury Duty',
};
export function leaveTypeLabel(type) {
    return LEAVE_TYPE_LABELS[type] ?? type;
}
/** Hours to two places, as the timesheet shows them. */
export function formatHours(hours) {
    return hours.toFixed(2);
}
/** Time since an instant, coarsely: "45m", "3h 12m", "2d 4h". */
export function formatElapsed(fromIso, nowMs) {
    const minutes = Math.max(0, Math.floor((nowMs - new Date(fromIso).getTime()) / 60000));
    if (minutes < 60)
        return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24)
        return `${hours}h ${minutes % 60}m`;
    return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}
/**
 * A clock-in time in the tenant's zone: "8:02 AM" today, "Tue 8:02 AM" earlier
 * this week, "Sep 15, 8:02 AM" before that, "Nov 25, 2025, 8:02 AM" in another
 * year. The zone is named when the viewer is somewhere else, so the time is
 * never read in the wrong one.
 */
export function formatClockTime(iso, timeZone, todayKey, viewerTimeZone) {
    const instant = new Date(iso);
    const time = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: 'numeric',
        minute: '2-digit',
        ...(viewerTimeZone !== timeZone && { timeZoneName: 'short' }),
    }).format(instant);
    const dayKey = localDayKey(instant, timeZone);
    if (dayKey === todayKey)
        return time;
    const daysAgo = differenceInCalendarDays(parseISO(todayKey), parseISO(dayKey));
    if (daysAgo > 0 && daysAgo < 7) {
        const weekday = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(instant);
        return `${weekday} ${time}`;
    }
    const date = new Intl.DateTimeFormat('en-US', {
        timeZone,
        month: 'short',
        day: 'numeric',
        ...(dayKey.slice(0, 4) !== todayKey.slice(0, 4) && { year: 'numeric' }),
    }).format(instant);
    return `${date}, ${time}`;
}
/** "Sep 18 – Oct 1" from two YYYY-MM-DD labels; years only when they differ. */
export function formatDateRange(startDate, endDate) {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const pattern = start.getFullYear() === end.getFullYear() ? 'MMM d' : 'MMM d, yyyy';
    return `${format(start, pattern)} – ${format(end, pattern)}`;
}
/** "Mon, Oct 5" or "Mon, Oct 5 – Tue, Oct 6" for a leave request's YYYY-MM-DD dates. */
export function formatLeaveDates(startDate, endDate) {
    const start = format(parseISO(startDate), 'EEE, MMM d');
    return startDate === endDate ? start : `${start} – ${format(parseISO(endDate), 'EEE, MMM d')}`;
}
/**
 * The pay period everyone is in, or null when employees are on different
 * schedules — in which case each row names its own.
 */
export function sharedPayPeriod(employees) {
    const periods = employees.flatMap((employee) => (employee.payPeriod ? [employee.payPeriod] : []));
    const [first] = periods;
    if (!first)
        return null;
    const allSame = periods.every((period) => period.startDate === first.startDate && period.endDate === first.endDate);
    return allSame ? { startDate: first.startDate, endDate: first.endDate, frequency: first.frequency } : null;
}
/** Salaried staff who have not punched this period: there are no hours to show. */
export function isUntracked(employee) {
    return employee.employmentType === 'exempt' && (employee.hours?.totalHours ?? 0) === 0;
}
function byName(a, b) {
    return a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName);
}
/** Most hours first; salaried staff with nothing tracked, and anyone without a period, last. */
export function orderByHours(employees) {
    const tracked = employees.filter((employee) => employee.hours && !isUntracked(employee));
    const rest = employees.filter((employee) => !employee.hours || isUntracked(employee));
    return [
        ...tracked.sort((a, b) => (b.hours?.totalHours ?? 0) - (a.hours?.totalHours ?? 0) || byName(a, b)),
        ...rest.sort(byName),
    ];
}
/**
 * Who is on the clock, split in two: shifts actually being worked (longest on
 * the clock first), and sessions left open so long they are almost certainly
 * a missed clock-out — still "clocked in" as far as the punch clock is
 * concerned, but not somebody working right now.
 */
export function splitOnTheClock(employees) {
    const open = employees.filter((employee) => employee.clock.status !== 'clocked_out');
    const bySince = (a, b) => (a.clock.since ?? '').localeCompare(b.clock.since ?? '') || byName(a, b);
    return {
        working: open.filter((employee) => !employee.clock.likelyMissedClockOut).sort(bySince),
        missedClockOut: open.filter((employee) => employee.clock.likelyMissedClockOut).sort(bySince),
    };
}
